<template>
  <CustomModal
    :title="isEditMode ? 'Update Project' : 'Create Project'"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <a-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      layout="vertical"
      :id="`${source}_add_project_section`"
      :required-mark="false"
      @finish="onFinish"
    >
      <a-form-item label="Name" name="name" class="mb-0">
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="Enter name"
          :autofocus="true"
        />
      </a-form-item>
    </a-form>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="creating"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          html-type="submit"
          :form="`${source}_add_project_section`"
          :loading="creating"
          :disabled="isSubmitDisabled"
        >
          {{
            creating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from "vue";
import { message } from "ant-design-vue";
import { projectEvents } from "@/src/constants/projectConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import ProjectService from "@/src/api/ProjectService.js";
import CustomModal from "@/src/components/CustomModal.vue";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
  project: { type: Object, default: null },
  mode: { type: String, default: "create" },
});

const emit = defineEmits(["update:visible"]);

const creating = ref(false);
const internalVisible = ref(props.visible);
const formRef = ref(null);
const nameInputRef = ref(null);

const formData = reactive({
  name: "",
  description: "",
});

const rules = {
  name: [
    {
      required: true,
      message: "Please enter project name",
      trigger: "change",
    },
  ],
};

const isEditMode = computed(() => {
  return props.mode === "edit";
});

const isSubmitDisabled = computed(() => {
  return !formData.name || formData.name.trim() === "" || creating.value;
});

const resetForm = () => {
  try {
    Object.assign(formData, {
      name: "",
      description: "",
    });
    creating.value = false;
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const onFinish = async () => {
  if (isEditMode.value) {
    await toUpdateProject();
  } else {
    await toCreateProject();
  }
};

const toCreateProject = async () => {
  try {
    creating.value = true;

    const payload = {
      name: formData.name,
      space_id: props.space?.id || 0,
    };

    const response = await ProjectService.tasks.createProject(payload);
    if (response?.success) {
      message.success(`Project ${formData.name} created successfully!`);

      const eventData = {
        event: projectEvents.CREATED,
        item: response.data.project,
        spaceId: props.space?.id || 0,
      };

      internalVisible.value = false;
      resetForm();

      eventBus.emit("project-manipulated", eventData);
    }
  } catch (error) {
    console.error("An error occurred while creating project: ", error);
    message.error("An error occurred while creating a project!");
  } finally {
    creating.value = false;
  }
};

const toUpdateProject = async () => {
  try {
    creating.value = true;

    const payload = {
      name: formData.name,
    };

    const response = await ProjectService.tasks.updateProject(
      props.project?.id,
      payload,
    );
    if (response?.success) {
      message.success(`Project renamed to ${formData.name} successfully!`);

      internalVisible.value = false;
      resetForm();

      eventBus.emit("project-manipulated", projectEvents.UPDATED);
    }
  } catch (error) {
    console.error("An error occurred while updating project: ", error);
    message.error("An error occurred while updating project!");
  } finally {
    creating.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
  { immediate: true },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);

onMounted(() => {
  if (isEditMode.value && props.project) {
    formData.name = props.project.name || "";
    formData.description = props.project.description || "";
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  }
});

watch(
  () => [props.project, props.visible, props.mode],
  ([project, visible, mode]) => {
    if (visible && mode === "edit" && project) {
      formData.name = project.name || "";
      formData.description = project.description || "";
      if (formRef.value) {
        formRef.value.clearValidate();
      }
    }
  },
  { immediate: true },
);
</script>
